import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import GalleriEditor from "@/components/GalleriEditor";

/**
 * Build 96.21 — återanvändbar "efter skapande"-panel.
 * Visar bekräftelse + befintlig GalleriEditor så nya poster direkt kan få bilder.
 *
 * Backend-Endpunkt bakom GalleriEditor: POST /api/mod/{collection}/{itemId}/galleri
 *   - Kräver mod-token (samma som skapade posten).
 *   - Endast BILDER (jpg/jpeg/png/webp/gif/heic/heif), max 20 MB, max 3 per post.
 *   - Videor stöds INTE av denna endpoint — därför visas ingen video-uppladdning här.
 *     YouTube-länk är oberoende via URL-fältet i redigeringsläget.
 *
 * Props:
 *   collection: "evenemang" | "foreningar" | "tjanstekort" | "vardagskort" | "karttipps"
 *   itemId: string (från create-svarets id)
 *   titel: string (visas i bekräftelsen)
 *   backTo: string (URL till listvy — knapp "Klar")
 *   backLabel: string (t.ex. "Tillbaka till moderatorvyn")
 *   publikt: boolean (för meddelandet)
 */
export default function PostCreateGalleri({ collection, itemId, titel, backTo, backLabel, publikt }) {
  const navigate = useNavigate();
  const [bilder, setBilder] = useState([]);

  return (
    <div
      data-testid="post-create-galleri"
      className="bg-pine/10 border border-pine/30 rounded-lg p-6 mt-4 flex flex-col gap-4"
    >
      <div className="flex items-start gap-2">
        <Check size={22} className="text-pine mt-1 shrink-0" />
        <div>
          <h2 className="font-heading text-xl font-medium text-ink mb-1">
            Sparat{titel ? `: ${titel}` : ""}
          </h2>
          <p className="text-sm text-bark/80">
            {publikt
              ? "Posten är sparad och publicerad. Du kan nu lägga till upp till 3 bilder som visas tillsammans med posten."
              : "Posten är sparad som utkast. Du kan lägga till upp till 3 bilder — de blir synliga när posten publiceras."}
          </p>
          <p className="text-xs text-bark/60 mt-1">
            Bilder: JPG, PNG, WebP, GIF, HEIC · max 20 MB · max 3 st. Video-uppladdning stöds inte här — använd YouTube-länk i redigeringsläget om du behöver film.
          </p>
        </div>
      </div>

      <div className="bg-white rounded border border-warm-border p-3">
        <GalleriEditor
          collection={collection}
          itemId={itemId}
          bilder={bilder}
          onChange={setBilder}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          data-testid="post-create-galleri-done"
          onClick={() => navigate(backTo)}
          className="bg-pine hover:bg-pine-hover text-white font-medium px-5 py-2.5 rounded-md text-sm transition-colors"
        >
          Klar
        </button>
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="text-muted-foreground hover:text-ink px-3 py-2.5 text-sm"
        >
          {backLabel || "Hoppa över och gå tillbaka"}
        </button>
      </div>
    </div>
  );
}
